import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import SidePanel, { SidePanelSection, SidePanelDivider } from '@/components/SidePanel';
import { MessageCircle, Send, Reply, Trash2, User, Edit2, Users, UserPlus, Lock, Globe, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate } from '@/utils/dateFormat';

/**
 * PROJECT CHAT - ROLE-BASED SEGREGATION
 * 
 * Chat Visibility Types:
 * - Internal: Only visible to Owner, Admin, Staff (private team discussions)
 * - Client: Visible to Client AND Internal team (client communication)
 * - Vendor: Visible to SPECIFIC Vendor AND Internal team (vendor-specific)
 * 
 * Access Rules:
 * - Internal users (owner/admin/staff): Can see ALL channels, send to any
 * - Clients: Can only see "client" channel
 * - Vendors: Can only see their own "vendor" channel
 */

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatMessageTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  if (diff < 24 * 60 * 60 * 1000 && date.getDate() === now.getDate()) {
    return timeStr;
  }
  
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.getDate() === yesterday.getDate()) {
    return 'Yesterday ' + timeStr;
  }
  
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return WEEKDAYS_SHORT[date.getDay()] + ' ' + timeStr;
  }
  
  return formatDate(date) + ' ' + timeStr;
};

const getRoleBadgeColor = (role) => {
  const colors = {
    owner: 'bg-amber-100 text-amber-700',
    admin: 'bg-purple-100 text-purple-700',
    staff: 'bg-blue-100 text-blue-700',
    project_manager: 'bg-sky-100 text-sky-700',
    client: 'bg-green-100 text-green-700',
    vendor: 'bg-orange-100 text-orange-700',
    consultant: 'bg-pink-100 text-pink-700',
    architect: 'bg-indigo-100 text-indigo-700',
    designer: 'bg-rose-100 text-rose-700',
    external_designer: 'bg-rose-100 text-rose-700',
  };
  return colors[role] || 'bg-stone-100 text-stone-700';
};

const getVisibilityIcon = (visibility) => {
  switch (visibility) {
    case 'internal': return <Lock size={12} className="text-stone-500" />;
    case 'client': return <Globe size={12} className="text-green-500" />;
    case 'vendor': return <Briefcase size={12} className="text-orange-500" />;
    default: return null;
  }
};

const getVisibilityLabel = (visibility) => {
  switch (visibility) {
    case 'internal': return 'Internal';
    case 'client': return 'Client';
    case 'vendor': return 'Vendor';
    default: return visibility;
  }
};

export default function ProjectChatPanel({ 
  projectId, 
  projectName, 
  project,
  isOpen, 
  onClose,
  onEditProject,
  users = []
}) {
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);
  
  // Chat channel state
  const [activeChannel, setActiveChannel] = useState('internal'); // internal, client, vendor
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isInternal = ['owner', 'admin', 'staff', 'project_manager'].includes(user.role);

  // Determine which channels the user can see
  const canSeeInternalChat = isInternal;
  const canSeeClientChat = isInternal || user.role === 'client';
  const canSeeVendorChat = isInternal || user.role === 'vendor';

  // For non-internal users, set their channel automatically
  useEffect(() => {
    if (!isInternal) {
      if (user.role === 'client') {
        setActiveChannel('client');
      } else if (user.role === 'vendor') {
        setActiveChannel('vendor');
        setSelectedVendorId(user.id);
      }
    }
  }, [user.role, isInternal]);

  const fetchMessages = useCallback(async () => {
    if (!projectId) return;
    
    try {
      let url = `${API}/projects/${projectId}/chat`;
      
      // For internal users, filter by active channel
      if (isInternal && activeChannel) {
        url += `?visibility=${activeChannel}`;
      }
      
      const response = await axios.get(url);
      
      // For vendor channel, filter by selected vendor
      let filteredMessages = response.data;
      if (activeChannel === 'vendor' && selectedVendorId) {
        filteredMessages = response.data.filter(m => 
          m.visible_to_vendor_id === selectedVendorId
        );
      }
      
      setMessages(filteredMessages);
    } catch (error) {
      console.error('Failed to fetch chat:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, activeChannel, selectedVendorId, isInternal]);

  const fetchVendors = useCallback(async () => {
    if (!projectId || !isInternal) return;
    
    try {
      const response = await axios.get(`${API}/projects/${projectId}/chat/vendors`);
      setVendors(response.data);
      
      // Auto-select first vendor if none selected
      if (!selectedVendorId && response.data.length > 0) {
        setSelectedVendorId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  }, [projectId, isInternal, selectedVendorId]);

  const fetchParticipants = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await axios.get(`${API}/projects/${projectId}/chat/participants?visibility=${activeChannel}`);
      setParticipants(response.data);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
    }
  }, [projectId, activeChannel]);

  useEffect(() => {
    if (isOpen && projectId) {
      setLoading(true);
      fetchMessages();
      fetchParticipants();
      if (isInternal) {
        fetchVendors();
      }
      
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [isOpen, projectId, fetchMessages, fetchParticipants, fetchVendors, isInternal]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const payload = {
        project_id: projectId,
        message: newMessage.trim(),
        reply_to: replyingTo?.id || null,
        attachments: [],
        visibility: activeChannel,
        visible_to_vendor_id: activeChannel === 'vendor' ? selectedVendorId : null,
      };
      
      const response = await axios.post(`${API}/projects/${projectId}/chat`, payload);
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      setReplyingTo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/chat/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Get client and consultant names
  const clientUser = users.find(u => u.id === project?.client_id);
  const consultantUsers = (project?.consultants || []).map(id => users.find(u => u.id === id)).filter(Boolean);

  // Get selected vendor name
  const selectedVendor = vendors.find(v => v.id === selectedVendorId);

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <MessageCircle size={20} className="text-blue-600" />
          <span>Project Chat</span>
          {isInternal && (
            <button
              onClick={() => setShowEditProject(!showEditProject)}
              className="ml-2 p-1 hover:bg-stone-200 rounded transition-colors"
              title="Edit Project"
            >
              <Edit2 size={16} className="text-stone-500" />
            </button>
          )}
        </div>
      }
      subtitle={projectName}
      minimized={panelMinimized}
      onMinimize={() => setPanelMinimized(!panelMinimized)}
    >
      {/* Channel Tabs - Only for Internal Users */}
      {isInternal && (
        <div className="px-4 pb-3 pt-1">
          <div className="flex gap-1 p-1 bg-stone-100 rounded-lg">
            <button
              onClick={() => setActiveChannel('internal')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeChannel === 'internal'
                  ? 'bg-white shadow text-stone-900'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
              data-testid="chat-tab-internal"
            >
              <Lock size={14} />
              <span>Internal</span>
            </button>
            <button
              onClick={() => setActiveChannel('client')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeChannel === 'client'
                  ? 'bg-white shadow text-green-700'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
              data-testid="chat-tab-client"
            >
              <Globe size={14} />
              <span>Client</span>
            </button>
            <button
              onClick={() => setActiveChannel('vendor')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeChannel === 'vendor'
                  ? 'bg-white shadow text-orange-700'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
              data-testid="chat-tab-vendor"
            >
              <Briefcase size={14} />
              <span>Vendor</span>
            </button>
          </div>
        </div>
      )}

      {/* Vendor Selector - Only when Vendor tab is active */}
      {activeChannel === 'vendor' && isInternal && (
        <div className="px-4 pb-3">
          <Select value={selectedVendorId || ''} onValueChange={setSelectedVendorId}>
            <SelectTrigger className="w-full" data-testid="vendor-selector">
              <SelectValue placeholder="Select a vendor..." />
            </SelectTrigger>
            <SelectContent>
              {vendors.length === 0 ? (
                <div className="p-2 text-sm text-stone-500">No vendors assigned to this project</div>
              ) : (
                vendors.map(vendor => (
                  <SelectItem key={vendor.id} value={vendor.id}>
                    <div className="flex items-center gap-2">
                      <span>{vendor.name}</span>
                      {vendor.unread_count > 0 && (
                        <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                          {vendor.unread_count}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Channel Info Banner */}
      <div className="px-4 pb-2">
        <div className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
          activeChannel === 'internal' ? 'bg-stone-100 text-stone-600' :
          activeChannel === 'client' ? 'bg-green-50 text-green-700' :
          'bg-orange-50 text-orange-700'
        }`}>
          {getVisibilityIcon(activeChannel)}
          <span>
            {activeChannel === 'internal' && 'Only visible to your team (Owner, Admin, Staff)'}
            {activeChannel === 'client' && `Visible to ${clientUser?.name || 'Client'} and your team`}
            {activeChannel === 'vendor' && `Visible to ${selectedVendor?.name || 'Selected Vendor'} and your team`}
          </span>
        </div>
      </div>

      {/* Inline Project Edit Section */}
      {showEditProject && (
        <>
          <SidePanelSection title="Edit Project Participants">
            <div className="space-y-4">
              <div>
                <Label className="text-stone-600">Client</Label>
                <p className="text-sm mt-1 p-2 bg-stone-50 rounded">
                  {clientUser ? (
                    <span className="flex items-center gap-2">
                      <User size={14} />
                      {clientUser.name} ({clientUser.email})
                    </span>
                  ) : (
                    <span className="text-stone-400">No client assigned</span>
                  )}
                </p>
              </div>
              
              <div>
                <Label className="text-stone-600">External Consultants</Label>
                {consultantUsers.length > 0 ? (
                  <div className="space-y-1 mt-1">
                    {consultantUsers.map(c => (
                      <p key={c.id} className="text-sm p-2 bg-stone-50 rounded flex items-center gap-2">
                        <User size={14} />
                        {c.name} ({c.role})
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm mt-1 p-2 bg-stone-50 rounded text-stone-400">
                    No consultants assigned
                  </p>
                )}
              </div>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  if (onEditProject) onEditProject();
                  setShowEditProject(false);
                }}
              >
                <UserPlus size={16} className="mr-2" />
                Manage Project Team
              </Button>
            </div>
          </SidePanelSection>
          <SidePanelDivider />
        </>
      )}

      {/* Chat Section */}
      <SidePanelSection title={`Messages (${messages.length})`}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-stone-400">Loading messages...</div>
          </div>
        ) : (
          <div className="flex flex-col h-[350px]">
            {/* Messages List */}
            <div className="flex-1 overflow-y-auto space-y-3 mb-4">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-stone-400">
                  <MessageCircle size={32} className="mx-auto mb-2" />
                  <p className="text-sm">
                    {activeChannel === 'internal' && 'No internal messages yet.'}
                    {activeChannel === 'client' && 'No messages with client yet.'}
                    {activeChannel === 'vendor' && (selectedVendorId ? 'No messages with this vendor yet.' : 'Select a vendor to view messages.')}
                  </p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isOwn = msg.sender_id === user.id;
                  const showSender = index === 0 || messages[index - 1]?.sender_id !== msg.sender_id;
                  const repliedMessage = msg.reply_to ? messages.find(m => m.id === msg.reply_to) : null;
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%]`}>
                        {showSender && !isOwn && (
                          <div className="flex items-center gap-2 mb-1 ml-1">
                            <span className="text-xs font-medium text-stone-600">{msg.sender_name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${getRoleBadgeColor(msg.sender_role)}`}>
                              {msg.sender_role}
                            </span>
                          </div>
                        )}
                        
                        {repliedMessage && (
                          <div className={`text-xs p-2 mb-1 rounded border-l-2 ${
                            isOwn ? 'bg-blue-50 border-blue-300' : 'bg-stone-100 border-stone-300'
                          }`}>
                            <p className="font-medium text-stone-500">{repliedMessage.sender_name}</p>
                            <p className="text-stone-400 truncate">{repliedMessage.message}</p>
                          </div>
                        )}
                        
                        <div
                          className={`group relative px-3 py-2 rounded-xl ${
                            isOwn
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : 'bg-stone-100 text-stone-900 rounded-bl-sm'
                          }`}
                        >
                          <p className="whitespace-pre-wrap break-words text-sm">{msg.message}</p>
                          <p className={`text-xs mt-1 ${isOwn ? 'text-blue-200' : 'text-stone-400'}`}>
                            {formatMessageTime(msg.created_at)}
                          </p>
                          
                          {/* Inline Actions */}
                          <div className={`absolute top-1 ${isOwn ? '-left-16' : '-right-16'} 
                            opacity-0 group-hover:opacity-100 transition-opacity flex gap-1`}>
                            <button
                              onClick={() => setReplyingTo(msg)}
                              className="p-1 bg-white rounded shadow hover:bg-stone-100"
                              title="Reply"
                            >
                              <Reply size={12} className="text-stone-500" />
                            </button>
                            {(isOwn || user.role === 'owner' || user.role === 'admin') && (
                              <button
                                onClick={() => handleDelete(msg.id)}
                                className="p-1 bg-white rounded shadow hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 size={12} className="text-red-500" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Preview */}
            {replyingTo && (
              <div className="px-2 py-2 mb-2 bg-stone-100 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs">
                  <Reply size={12} className="text-blue-500" />
                  <span className="text-stone-500">Replying to {replyingTo.sender_name}</span>
                </div>
                <button 
                  onClick={() => setReplyingTo(null)} 
                  className="p-1 hover:bg-stone-200 rounded"
                >
                  <span className="text-stone-500">×</span>
                </button>
              </div>
            )}

            {/* Message Input */}
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  activeChannel === 'internal' ? 'Type internal message...' :
                  activeChannel === 'client' ? 'Type message to client...' :
                  selectedVendorId ? 'Type message to vendor...' : 'Select a vendor first...'
                }
                className="flex-1"
                disabled={sending || (activeChannel === 'vendor' && !selectedVendorId)}
                data-testid="chat-message-input"
              />
              <Button 
                onClick={handleSend} 
                disabled={!newMessage.trim() || sending || (activeChannel === 'vendor' && !selectedVendorId)}
                size="sm"
                className="px-3"
                data-testid="chat-send-button"
              >
                <Send size={16} />
              </Button>
            </div>
          </div>
        )}
      </SidePanelSection>

      {/* Participants Section */}
      <>
        <SidePanelDivider />
        <SidePanelSection title={`Participants (${participants.length})`}>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {participants.map(p => (
              <div key={p.id} className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-stone-200 flex items-center justify-center">
                  <User size={12} className="text-stone-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${getRoleBadgeColor(p.role)}`}>
                  {p.role}
                </span>
              </div>
            ))}
          </div>
        </SidePanelSection>
      </>
    </SidePanel>
  );
}
